import {useCallback, useEffect, useState} from 'react';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {useAppDispatch, useAppSelector} from '@/store';
import {CreatePointMultiplierSchemas} from '@/services/schema/PointMultiplier/CreatePointMultiplier';
import {getISOTimeStringForReview} from '@/utils/commons';
import {ROUTES} from '@/utils/constants/routesConstants';
import {STATUS_VALUES} from '@/utils/constants/commonConstants';
import {useNavigate} from 'react-router-dom';
import {resetPointMultiplier} from '@/store/slices/pointMultiplier';
import {useSelector} from 'react-redux';
import {getPointMultiplierProductsCsvFile} from '@/services/pointMultiplier';
import {downloadCSVFiles} from '@/helpers/shadcn/utils';
import enUS from '@/internationalization/i18n/en-us';

export const useCreatePointMultiplier = () => {
  const pointMultiplier = useAppSelector((state) => state?.pointMultiplier);
  const countryCode = useSelector((state) => state.countryCode.countryCode);
  const countyName = enUS.COUNTRIES[countryCode];
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState(pointMultiplier);
  const [formError, setFormError] = useState({
    modifierName: false,
    rulesCriteria: false,
    hasEndDate: false,
    rulesPriority: false,
    segment: false,
    category: false,
    channel: false,
    multiplierValue: false,
    activeStartDate: false,
    activeEndDate: false
  });
  const [disableNext, setDisableNext] = useState(true);
  const {dropdownList: dropdownListRulesCriteria} = useDropdown({
    dropdownType: DROPDOWN_TYPES.PointMultiplierRulesCriteria
  });
  const {dropdownList: dropdownListSegment} = useDropdown({
    dropdownType: DROPDOWN_TYPES.PointMultiplierSegment
  });
  const {dropdownList: dropdownListCategory} = useDropdown({
    dropdownType: DROPDOWN_TYPES.PointMultiplierCategory
  });
  const {dropdownList: dropdownListChannel} = useDropdown({
    dropdownType: DROPDOWN_TYPES.PointMultiplierChannel
  });
  const [isCancel, setIsCancel] = useState(false);
  const [isNameValid, setIsNameValid] = useState(true);

  const [rulesCriteriaList, setRulesCriteriaList] = useState({});
  const [segmentList, setSegmentList] = useState({});
  const [categoryList, setCategoryList] = useState({});
  const [channelList, setChannelList] = useState({});
  const [progressBarSteps, setProgressBarSteps] = useState([
    {title: 'Setup'},
    {title: 'Review'}
  ]);
  const createReqObj = (status = STATUS_VALUES.Published) => {
    let obj = {
      multiplierName: formData?.modifierName,
      rulesPriority: formData?.rulesPriority,
      multiplierValue: formData?.multiplierValue,
      startDate: getISOTimeStringForReview(
        formData?.activeStartDate,
        false,
        true
      ),
      status,
      uuid: formData?.uuid,
      id: formData?.id
    };
    if (formData?.hasEndDate) {
      obj['endDate'] = getISOTimeStringForReview(
        formData?.activeEndDate,
        true,
        false
      );
    }
    if (formData?.rulesCriteria == 'Products') {
      return {
        ...obj,
        startDate: getISOTimeStringForReview(
          formData?.activeStartDate,
          false,
          true
        ),
        status,
        rulesCriteria: formData?.rulesCriteria,
        segment: formData?.segment,
        productMultipliers: formData?.productMultipliers,
        multiplierValue: null,
        metadata: {
          productFileName: formData?.uploadOutcome?.fileName
        }
      };
    } else if (formData?.rulesCriteria == 'Segment') {
      return {
        ...obj,
        rulesCriteria: formData?.rulesCriteria,
        segment: formData?.segment
      };
    } else if (formData?.rulesCriteria == 'Category') {
      return {
        ...obj,
        rulesCriteria: formData?.rulesCriteria,
        category: formData?.category
      };
    } else if (formData?.rulesCriteria == 'Channel') {
      return {
        ...obj,
        rulesCriteria: 'Channel',
        channel:
          formData?.channel == 'Both (Online & Offline)'
            ? 'Both'
            : formData?.channel
      };
    }
  };
  const isEmpty = (val, message) => {
    const empty = val === undefined || val === '';
    return empty ? message : false;
  };
  const checkMinValue = (minVal) => {
    return (val, message) => {
      if (val < minVal) return message;
      else return false;
    };
  };
  const removeSuffixAndCheckForMin = (minVal) => {
    return (val, message) => {
      try {
        val = val.replace(/x/gi, '');
        if (isNaN(val)) {
          return message;
        }
        if (val < minVal) return message;
        else return false;
      } catch {
        return false;
      }
    };
  };
  const handleUpdateValue = (val, key, isError = false) => {
    const errorData = {[key]: isError};
    if (key == 'rulesCriteria') {
      errorData['semgent'] =
        errorData['channel'] =
        errorData['category'] =
          false;
    }
    setFormError((prev) => ({...prev, ...errorData}));
    if (
      key == 'rulesPriority' ||
      key == 'modifierName' ||
      key == 'hasEndDate' ||
      key == 'activeStartDate' ||
      key == 'activeEndDate'
    ) {
      if (key != 'hasEndDate') val = val?.trim();
      setFormData((prev) => {
        validateFormData(
          {...prev, [key]: val},
          formFields.filter((f) => f.name == key)
        );
        return {...prev, [key]: val};
      });
    } else if (
      key === 'multiplierValue' &&
      formData?.rulesCriteria !== 'Products'
    ) {
      val = val?.replace(/x/gi, '');
      let v = val?.replace(/[^0-9.]/gi, '');
      setFormData((prev) => {
        validateFormData(
          {...prev, [key]: v},
          formFields.filter((f) => f.name == key)
        );
        return {...prev, [key]: v};
      });
    } else if (key === 'segment' && typeof val === 'object') {
      const segmentValue = val?.[0]?.displayText;
      const data = {[key]: segmentValue || undefined};

      setFormData((prev) => {
        validateFormData(
          {...prev, ...data},
          formFields.filter((f) => f.name === key)
        );
        return {...prev, ...data};
      });
    } else {
      const data = {[key]: val?.[0]?.displayText};
      if (val?.length > 0) {
        if (key == 'rulesCriteria') {
          data['segment'] = undefined;
          data['channel'] = undefined;
          data['category'] = undefined;
        }
        setFormData((prev) => {
          validateFormData(
            {...prev, ...data},
            formFields.filter(
              (f) =>
                f.name == key ||
                ['segment', 'category', 'channel'].indexOf(f.name) >= 0
            )
          );
          return {...prev, ...data};
        });
      }
    }
  };
  const isPastDate = (val, message) => {
    if (!val) return false;
    const [mm, dd, yyyy] = val.split('/').map(Number);
    const selectedDate = new Date(yyyy, mm - 1, dd);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // clear time portion
    return selectedDate > today ? false : message;
  };
  const isInValidEndDate = (endDate, message) => {
    if (!formData?.hasEndDate) return false;
    if (!formData?.activeStartDate) {
      return 'Active date should be selected';
    }
    if (endDate == '') {
      return message;
    }
    const parseDate = (str) => {
      const [mm, dd, yyyy] = str.split('/').map(Number);
      return new Date(yyyy, mm - 1, dd);
    };
    const eDate = parseDate(endDate);
    const sDate = parseDate(formData?.activeStartDate);
    return eDate < sDate ? message : false;
  };
  const shouldBeNumber = (val, message) => {
    return !isNaN(val) && Number.isInteger(Number(val)) ? false : message;
  };
  const formFields = [
    {
      name: 'modifierName',
      validators: [
        {
          message: 'Multiplier name is required',
          validator: isEmpty
        }
      ]
    },
    {
      name: 'segment',
      validators: [
        {
          message: 'Segment  is required',
          validator: isEmpty
        }
      ]
    },
    {
      name: 'category',
      validators: [
        {
          message: 'Category  is required',
          validator: isEmpty
        }
      ]
    },
    {
      name: 'channel',
      validators: [
        {
          message: 'Channel is required',
          validator: isEmpty
        }
      ]
    },
    {
      name: 'rulesPriority',
      validators: [
        {
          message: 'Rules priority is required',
          validator: isEmpty
        },
        {
          message: 'Min value should be 1',
          validator: checkMinValue(1)
        },
        {
          message: 'rules priority should be a number',
          validator: shouldBeNumber
        }
      ]
    },
    {
      name: 'multiplierValue',
      validators: [
        {
          message: 'Multiplier value is required',
          validator: isEmpty
        },
        {
          message: 'Min value multiplier should be greater than 0x',
          validator: removeSuffixAndCheckForMin(1)
        }
      ]
    },
    {
      name: 'activeStartDate',
      validators: [
        {
          message: ' Active start date can only start from a date in future',
          validator: isPastDate
        }
      ]
    },
    {
      name: 'activeEndDate',
      validators: [
        {
          message: 'Active end date is required when toggle was selected',
          validator: isEmpty
        },
        {
          validator: isInValidEndDate,
          message: 'Should be greater than active start date'
        }
      ]
    }
  ];
  const {
    rulesCriteriaJsonSchema,
    segmentJsonSchema,
    categoryJsonSchema,
    channelJsonSchema,
    rulesCriteriaUISchema
  } = CreatePointMultiplierSchemas;
  const validateFormData = (
    formDataDetails = formData,
    formFieldsArr = formFields
  ) => {
    const error = {};
    let isValidForm = true;

    // Helper function to filter form fields based on conditions
    const filterFormFields = (fields, condition, additionalFields = []) => {
      return [...fields.filter(condition), ...additionalFields];
    };

    // Filter form fields based on rulesCriteria and multiplierValue
    if (formDataDetails['rulesCriteria'] !== 'Products') {
      formFieldsArr = filterFormFields(
        formFieldsArr,
        (f) => f.name !== 'multiplierValue',
        formFields.filter(
          (f) =>
            f.name === 'multiplierValue' &&
            formDataDetails['multiplierValue'] !== undefined
        )
      );
    }

    // Filter form fields based on rulesPriority
    formFieldsArr = filterFormFields(
      formFieldsArr,
      (f) => f.name !== 'rulesPriority',
      formFields.filter(
        (f) =>
          f.name === 'rulesPriority' &&
          formDataDetails['rulesPriority'] !== undefined
      )
    );

    // Helper function to validate a single field
    const validateField = (field, formData) => {
      const fieldErrors = [];
      for (const isValid of field.validators) {
        const validationResult = isValid.validator(
          formData[field.name],
          isValid.message
        );
        if (validationResult !== false) {
          fieldErrors.push(validationResult);
          if (
            !['segment', 'category', 'channel', 'activeEndDate'].includes(
              field.name
            )
          ) {
            isValidForm = false;
          }
          break;
        }
      }
      return fieldErrors.length > 0 ? fieldErrors : false;
    };

    // Validate each field
    formFieldsArr.forEach((field) => {
      if (
        formDataDetails.rulesCriteria === 'Products' &&
        ['segment', 'category', 'channel', 'multiplierValue'].includes(
          field.name
        )
      ) {
        error[field.name] = false;
        return;
      }
      error[field.name] = validateField(field, formDataDetails);
    });

    // Additional validations for specific fields
    if (
      formDataDetails.rulesCriteria !== 'Products' &&
      error['segment'] &&
      error['category'] &&
      error['channel']
    ) {
      isValidForm = false;
    }
    if (formDataDetails['hasEndDate'] && error['activeEndDate']) {
      isValidForm = false;
    }

    // Update form errors and return validation result
    setFormError((prev) => ({...prev, ...error}));
    return isValidForm;
  };
  const handleSetRulesCriteriaList = useCallback(
    (rulesCriteriaJsonSchema, dropdownListRulesCriteria) => {
      if (!dropdownListRulesCriteria) return;
      if (rulesCriteriaJsonSchema && dropdownListRulesCriteria?.length > 0) {
        let updatedDropdownData = JSON.parse(
          JSON.stringify(rulesCriteriaJsonSchema)
        );
        let filteredDropdownList = [...dropdownListRulesCriteria];

        if (filteredDropdownList.some((opt) => opt.value === 'Products')) {
          if (countryCode !== 'EG') {
            filteredDropdownList = filteredDropdownList.filter(
              (opt) => opt.value !== 'Products'
            );
          }
        }

        if (updatedDropdownData?.properties?.rulesCriteria) {
          updatedDropdownData.properties.rulesCriteria.options =
            filteredDropdownList;
        }
        setRulesCriteriaList(updatedDropdownData);
      }
    },
    [countryCode]
  );
  const handleSetSegmentList = useCallback(
    (segmentJsonSchema, dropdownListSegment) => {
      if (!dropdownListSegment) return;
      if (segmentJsonSchema && dropdownListSegment?.length > 0) {
        if (formData?.rulesCriteria?.trim()?.toLowerCase() == 'products') {
          dropdownListSegment = [
            {displayText: '-', value: '-'},
            ...dropdownListSegment
          ];
          if (formData['segment'] === undefined || formData['segment'] === '') {
            formData['segment'] = dropdownListSegment[0]?.displayText;
          }
        }
        let updatedDropdownData = JSON.parse(JSON.stringify(segmentJsonSchema));
        if (updatedDropdownData?.properties?.segment) {
          updatedDropdownData.properties.segment.options = [
            ...dropdownListSegment
          ];
        }
        setSegmentList(updatedDropdownData);
      }
    },
    [formData]
  );
  const handleSetCategoryList = (categoryJsonSchema, dropdownList) => {
    if (!dropdownList) return;
    if (categoryJsonSchema && dropdownList?.length > 0) {
      let updatedDropdownData = JSON.parse(JSON.stringify(categoryJsonSchema));
      if (updatedDropdownData?.properties?.category) {
        updatedDropdownData.properties.category.options = [...dropdownList];
      }
      setCategoryList(updatedDropdownData);
    }
  };

  const handleSetChannelList = (channelJsonSchema, dropdownList) => {
    if (!dropdownList) return;
    if (channelJsonSchema && dropdownList?.length > 0) {
      let updatedDropdownData = JSON.parse(JSON.stringify(channelJsonSchema));
      if (updatedDropdownData?.properties?.channel) {
        updatedDropdownData.properties.channel.options = [...dropdownList];
      }
      setChannelList(updatedDropdownData);
    }
  };

  const handleCancel = () => {
    setIsCancel(!isCancel);
  };

  const handleValidationModalClose = () => {
    setIsNameValid(true);
  };

  const handleResetFormData = () => {
    dispatch(resetPointMultiplier());
  };

  const onClickPrimaryButton = () => {
    handleResetFormData();
    navigate(ROUTES.POINT_MULTIPLIER);
  };

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel);
  };

  useEffect(() => {
    handleSetRulesCriteriaList(
      rulesCriteriaJsonSchema,
      dropdownListRulesCriteria
    );
  }, [
    rulesCriteriaJsonSchema,
    dropdownListRulesCriteria,
    handleSetRulesCriteriaList
  ]);

  useEffect(() => {
    handleSetSegmentList(segmentJsonSchema, dropdownListSegment);
  }, [
    segmentJsonSchema,
    dropdownListSegment,
    formData?.rulesCriteria,
    handleSetSegmentList
  ]);

  useEffect(() => {
    handleSetCategoryList(categoryJsonSchema, dropdownListCategory);
  }, [categoryJsonSchema, dropdownListCategory]);

  useEffect(() => {
    handleSetChannelList(channelJsonSchema, dropdownListChannel);
  }, [channelJsonSchema, dropdownListChannel]);

  useEffect(() => {
    if (formData?.rulesCriteria?.trim()?.toLowerCase() === 'products') {
      setProgressBarSteps([
        {title: 'Setup'},
        {title: 'Products'},
        {title: 'Review'}
      ]);
    } else {
      setProgressBarSteps([{title: 'Setup'}, {title: 'Review'}]);
    }
  }, [formData?.rulesCriteria]);

  return {
    formData,
    setFormData,
    formError,
    formFields,
    setFormError,
    disableNext,
    setDisableNext,
    validateFormData,
    handleUpdateValue,
    rulesCriteriaJsonSchema: rulesCriteriaList,
    segmentJsonSchema: segmentList,
    categoryJsonSchema: categoryList,
    channelJsonSchema: channelList,
    rulesCriteriaUISchema,
    createReqObj,
    progressBarSteps,
    isCancel,
    isNameValid,
    setIsNameValid,
    handleCancel,
    handleValidationModalClose,
    onClickPrimaryButton,
    onClickSecondaryButton,
    countyName
  };
};

export const usePointMultiplierDownload = ({
  pointMultiplierId,
  formData,
  fileName
}) => {
  const handleDownload = () => {
    if (pointMultiplierId) {
      getPointMultiplierProductsCsvFile(pointMultiplierId).then((response) => {
        if (response) {
          const csvRows = response?.split('\n');
          const csvString = csvRows?.join('\n');
          downloadCSVFiles(csvString, fileName);
        }
      });
    } else {
      const multipliers = formData.productMultipliers || [];
      const csvRows = [
        ['Product ID', 'Multiplier'],
        ...multipliers.map((item) => [item.productId, item.multiplier])
      ];
      const csvString = csvRows.map((row) => row.join(',')).join('\n');
      downloadCSVFiles(csvString, fileName);
    }
  };

  return {handleDownload};
};
